import { queryString } from '@shared/hooks/recoil-query';
import { api_ } from '@shared/plugIn/axios';
import { LowerRole } from '@shared/types';

import { GiftStoreRole } from '../giftOrder/giftOrder.interface';
import {
  DeliveryChannel,
  GiftCoupon,
  GiftCouponForPublic,
  GiftCouponForStore,
  GiftCouponRedeemStatus,
  GiftCouponRequestDto,
  GiftCouponSaveRecipientDto,
  GiftCouponShareStatus,
  GiftCouponStatistics,
  GiftCouponSummary,
  PendingChargeEstimateResponse,
  ShareChannel,
  VerificationConfirmDto,
  VerificationConfirmResponse,
  VerificationSendDto,
  VerificationSendResponse,
} from './giftCoupon.interface';

// 쿠폰 단건 조회 (Public) — 카카오 공유 후 shareStatus / kakaoSharedAt 가벼운 폴링용
export const getGiftCouponById = async (id: string): Promise<GiftCouponForPublic> => {
  const { data } = await api_.get<GiftCouponForPublic>(
    `/${LowerRole.Public}/gift-coupon/${id}`,
  );
  return data;
};

/**
 * 공유 처리 (ShareStatus: NotShared → Shared)
 *
 * shareChannel을 함께 보내면 백엔드가 lastSharedChannel을 갱신하고
 * shareHistory에도 기록합니다. 카카오톡은 웹훅으로도 자동 보정됩니다.
 */
export const shareCoupon = async (
  id: string,
  role: GiftStoreRole,
  shareChannel?: ShareChannel,
): Promise<GiftCouponForStore> => {
  const response = await api_.post<GiftCouponForStore>(
    `/${role}/gift-coupon/share/${id}`,
    shareChannel ? { shareChannel } : {},
  );
  return response.data;
};

// 공유 취소 (ShareStatus: Shared → NotShared)
export const unshareCoupon = async (
  id: string,
  role: GiftStoreRole,
): Promise<GiftCouponForStore> => {
  const response = await api_.post<GiftCouponForStore>(
    `/${role}/gift-coupon/unshare/${id}`,
  );
  return response.data;
};

// 전달 확인 (confirmedAt 기록 + memo optional)
export const confirmCoupon = async (
  id: string,
  role: GiftStoreRole,
  memo?: string,
): Promise<GiftCouponForStore> => {
  const response = await api_.post<GiftCouponForStore>(
    `/${role}/gift-coupon/confirm-share/${id}`,
    memo ? { memo } : {},
  );
  return response.data;
};

// 조회 처리 (ShareStatus: Shared → Viewed)
export const viewCoupon = async (
  id: string,
  role: GiftStoreRole = LowerRole.Public,
): Promise<GiftCouponForStore> => {
  const response = await api_.post<GiftCouponForStore>(`/${role}/gift-coupon/view/${id}`);
  return response.data;
};

// 교환 신청 (RedeemStatus: Available → Requested)
export const requestCouponRedemption = async (
  id: string,
  dto: GiftCouponRequestDto,
  role: GiftStoreRole = LowerRole.Public,
): Promise<GiftCouponForStore> => {
  const response = await api_.post<GiftCouponForStore>(
    `/${role}/gift-coupon/request/${id}`,
    dto,
  );
  return response.data;
};

// 나중에 신청하기 — 수신자 번호만 미리 저장
export const saveRecipient = async (
  id: string,
  dto: GiftCouponSaveRecipientDto,
  role: GiftStoreRole = LowerRole.Public,
): Promise<GiftCouponForStore> => {
  const response = await api_.post<GiftCouponForStore>(
    `/${role}/gift-coupon/save-recipient/${id}`,
    dto,
  );
  return response.data;
};

// 발송 처리 (RedeemStatus: Requested → Delivered)
export interface DeliverCouponDto {
  priceWhole: number;
  priceRetail: number;
  priceRebate: number;
  /** 미선택 시 빈 문자열로 전달 */
  deliveryChannel: DeliveryChannel | '';
}

export const deliverCoupons = async (
  couponIds: string[],
  dto: DeliverCouponDto,
): Promise<GiftCouponForStore[]> => {
  const response = await api_.post<GiftCouponForStore[]>(
    `/${LowerRole.Admin}/gift-coupon/deliver`,
    {
      couponIds,
      priceWhole: dto.priceWhole,
      priceRetail: dto.priceRetail,
      priceRebate: dto.priceRebate,
      deliveryChannel: dto.deliveryChannel,
    },
  );
  return response.data;
};

export const deliverCoupon = async (
  id: string,
  dto: DeliverCouponDto,
): Promise<GiftCouponForStore> => {
  const results = await deliverCoupons([id], dto);
  return results[0] as GiftCouponForStore;
};

/**
 * 발송완료 취소 (Delivered → Requested) — 매체 발송 실패·실수 처리 정정.
 * 쿠폰은 발송대기로 복귀(gifticon 제거)하고, 원장 기장분은 Restore(+)로 자동 환급된다
 * (restored = 실제 환급 기장 요약 — 원장 도입 전 발송분은 빠짐).
 * 전체 검증 방식: 한 건이라도 불가(비Delivered·정산서 포함)면 전체 400.
 * 스펙: docs/gift-coupon-undeliver-spec.md · 구서버엔 없어 404 가능(호출부 가드).
 */
export interface UndeliverCouponsResponse {
  undelivered: number;
  restored: Array<{ channel: DeliveryChannel; amount: number; quantity: number }>;
}

export const undeliverCoupons = async (
  couponIds: string[],
  reason: string,
): Promise<UndeliverCouponsResponse> => {
  const response = await api_.post<UndeliverCouponsResponse>(
    `/${LowerRole.Admin}/gift-coupon/undeliver`,
    { couponIds, reason },
  );
  return response.data;
};

/**
 * 대리발송(신청 생략 즉시발송) — 관리자가 연락처를 받아 수령자 신청(번호인증·서명) 없이
 * 대리신청 + 발송완료를 한 번에 처리. Available 쿠폰은 phoneNumber 필수,
 * Requested 쿠폰은 생략(기존 recipient 유지). 가격·매체는 배치 공통이므로
 * 같은 giftItem 단위로만 묶어 호출할 것.
 * 스펙: docs/gift-coupon-proxy-deliver-spec.md · 백엔드 구현 완료(2026-07-14),
 * 404 가드는 구서버 안전장치로 유지.
 */
export interface ProxyDeliverEntry {
  couponId: string;
  /** 숫자만 (01012345678). Available 쿠폰이면 필수 */
  phoneNumber?: string;
  /**
   * 선택형 상품일 때 실제 발송한 choiceUnit _id — Available 쿠폰의 selectedChoiceUnit에
   * 기록해 "무슨 상품을 대리발송했는지" 남긴다. 백엔드 반영 완료(스펙 §7, 2026-07-14).
   * 구서버는 이 필드를 무시(발송 정상, 기록만 누락)라 하위호환 안전.
   */
  selectedChoiceUnit?: string;
}

export interface ProxyDeliverDto {
  entries: ProxyDeliverEntry[];
  priceWhole: number;
  priceRetail: number;
  priceRebate: number;
  deliveryChannel: DeliveryChannel;
}

export interface ProxyDeliverResult {
  deliveredCount: number;
  coupons?: GiftCouponForStore[];
}

export const proxyDeliverCoupons = async (
  dto: ProxyDeliverDto,
): Promise<ProxyDeliverResult> => {
  const { data } = await api_.post<ProxyDeliverResult>(
    `/${LowerRole.Admin}/gift-coupon/proxy-deliver`,
    dto,
  );
  return data;
};

// 발송불가 처리 (RedeemStatus: Requested → Rejected)
export const rejectCoupon = async (id: string): Promise<GiftCouponForStore> => {
  const response = await api_.patch<GiftCouponForStore>(`/admin/gift-coupon/${id}`, {
    redeemStatus: GiftCouponRedeemStatus.Rejected,
  });
  return response.data;
};

// 수신자 전화번호 수정 (Admin)
export const updateRecipientPhone = async (
  id: string,
  phoneNumber: string,
): Promise<GiftCouponForStore> => {
  const response = await api_.patch<GiftCouponForStore>(`/admin/gift-coupon/${id}`, {
    'recipient.phoneNumber': phoneNumber,
  });
  return response.data;
};

// Admin에서 공유 상태를 NotShared + 교환 상태를 Available로 초기화
export const resetCouponToNotShared = async (id: string): Promise<GiftCouponForStore> => {
  const response = await api_.patch<GiftCouponForStore>(`/admin/gift-coupon/${id}`, {
    shareStatus: GiftCouponShareStatus.NotShared,
    redeemStatus: GiftCouponRedeemStatus.Available,
  });
  return response.data;
};

// 쿠폰 통계 조회 (Admin)
export const fetchGiftCouponStatistics = async (): Promise<GiftCouponStatistics> => {
  const response = await api_.get<GiftCouponStatistics>(
    `/${LowerRole.Admin}/gift-coupon/statistics`,
  );
  return response.data;
};

// 발송대기 매체별 충전 필요액 예측 (Admin) — 구서버엔 없어 404 가능(호출부 가드)
export const getPendingChargeEstimate =
  async (): Promise<PendingChargeEstimateResponse> => {
    const response = await api_.get<PendingChargeEstimateResponse>(
      `/${LowerRole.Admin}/gift-coupon/pending-charge-estimate`,
    );
    return response.data;
  };

/** 발송 통계(데이터 분석) lean 행 — 발송 스냅샷과 상품 참조만 */
export interface DeliveredCouponStatRow {
  _id: string;
  giftItem?:
    | {
        _id: string;
        name?: string;
        giftItemInfo?: { _id: string; name?: string } | string;
      }
    | string;
  selectedChoiceUnit?: { _id: string; name?: string } | string;
  gifticon?: {
    deliveredAt?: Date;
    priceWhole?: number;
    deliveryChannel?: DeliveryChannel;
  };
}

/**
 * 발송 통계용 기간 내 Delivered 쿠폰 lean 조회 (Admin) — 기존 search 재사용, 백엔드 무수정.
 * 통계 탭이 무거울 수 있어 탭을 열 때만 호출한다.
 */
export const searchDeliveredCouponsForStats = async (
  from: string,
  to: string,
): Promise<DeliveredCouponStatRow[]> => {
  const query = queryString({
    filter: {
      redeemStatus: GiftCouponRedeemStatus.Delivered,
      'gifticon.deliveredAt': {
        $gte: new Date(`${from}T00:00:00+09:00`).toISOString(),
        $lte: new Date(`${to}T23:59:59.999+09:00`).toISOString(),
      },
    },
    populate: [
      {
        path: 'giftItem',
        select: 'name giftItemInfo',
        populate: [{ path: 'giftItemInfo', select: 'name' }],
      },
      { path: 'selectedChoiceUnit', select: 'name' },
    ],
  });
  // select는 공백 구분 문자열이라 queryString(encode:false) 대신 직접 인코딩해 붙인다
  const select = encodeURIComponent(
    'giftItem selectedChoiceUnit gifticon.deliveredAt gifticon.priceWhole gifticon.deliveryChannel',
  );
  const response = await api_.get<DeliveredCouponStatRow[]>(
    `/${LowerRole.Admin}/gift-coupon/search?${query}&select=${select}`,
  );
  return response.data;
};

/**
 * 쿠폰 교환신청자(= 실제 수령자) 최소 필드 조회 — CS 응대 로그 보강용.
 * 기존 search 재사용(백엔드 무수정)이고, 라우트는 `@Roles(Admin, GiftAgent)` 라
 * 마리 계정에서도 401로 튕기지 않는다.
 *
 * ⚠️ CS 응대 로그(`/admin/gift-cs-memo/log`) 요약에는 쿠폰 수령자가 아직 실리지 않는다.
 * 서버가 `coupon.recipientName` 을 내려주기 시작하면 호출부가 이 조회를 건너뛴다
 * (요청: docs/shop-order-cs-memo-log-enrich-spec.md §10).
 */
export interface CouponRecipientInfo {
  name?: string;
  phoneNumber?: string;
  requestedAt?: Date;
  /** 전달자(구매자)가 받는 사람을 가리키려고 남긴 메모 — 이름이 없는 자리를 대신한다 */
  memo?: string;
  /** 번호 인증 기록 */
  verification?: { phoneNumber?: string; verifiedAt?: Date };
  /** 서명 이미지 URL (버킷 키) */
  signatureImageUrl?: string;
}

interface CouponRecipientRow {
  _id: string;
  recipient?: {
    name?: string;
    phoneNumber?: string;
    requestedAt?: Date;
    //* populate 하면 객체, 안 하면 id 문자열
    signatureImage?: { _id?: string; url?: string } | string;
  };
  memo?: string;
  verification?: { phoneNumber?: string; verifiedAt?: Date };
  /**
   * 재발행(추가) 쿠폰의 수신자 번호 — 교환신청 전에 관리자가 적어두는 값이라
   * `recipient`가 통째로 없는 건에서도 번호만은 여기 있다. 이름은 없다.
   */
  additionalRecipientPhone?: string;
}

//* URL 길이 상한을 넘기지 않게 끊는다 (ObjectId 24자 × 100 ≈ 4.5KB)
const COUPON_RECIPIENT_CHUNK = 100;

export const searchCouponRecipients = async (
  couponIds: string[],
): Promise<Record<string, CouponRecipientInfo>> => {
  const ids = Array.from(new Set(couponIds.filter(Boolean)));
  if (ids.length === 0) return {};

  const chunks: string[][] = [];
  for (let i = 0; i < ids.length; i += COUPON_RECIPIENT_CHUNK) {
    chunks.push(ids.slice(i, i + COUPON_RECIPIENT_CHUNK));
  }

  // select는 공백 구분 문자열이라 queryString 대신 직접 인코딩해 붙인다
  const select = encodeURIComponent(
    'recipient.name recipient.phoneNumber recipient.requestedAt recipient.signatureImage ' +
      'memo verification.phoneNumber verification.verifiedAt additionalRecipientPhone',
  );
  const pages = await Promise.all(
    chunks.map(async (chunk) => {
      const query = queryString({
        filter: { _id: { $in: chunk } },
        //* 서명은 ref라 populate 없이는 id만 온다 (url이 있어야 화면에 띄운다)
        populate: [{ path: 'recipient.signatureImage', select: 'url' }],
      });
      const { data } = await api_.get<CouponRecipientRow[]>(
        `/${LowerRole.Admin}/gift-coupon/search?${query}&select=${select}`,
      );
      return data;
    }),
  );

  const map: Record<string, CouponRecipientInfo> = {};
  for (const row of pages.flat()) {
    if (!row?._id) continue;
    const signature = row.recipient?.signatureImage;
    const info: CouponRecipientInfo = {
      ...(row.recipient?.name ? { name: row.recipient.name } : {}),
      //* 이름 없이 번호만 아는 건(재발행 쿠폰)도 "누구 번호인지"는 답할 수 있어야 한다
      ...(row.recipient?.phoneNumber || row.additionalRecipientPhone
        ? {
            phoneNumber: row.recipient?.phoneNumber ?? row.additionalRecipientPhone ?? '',
          }
        : {}),
      ...(row.recipient?.requestedAt ? { requestedAt: row.recipient.requestedAt } : {}),
      ...(row.memo ? { memo: row.memo } : {}),
      ...(row.verification ? { verification: row.verification } : {}),
      ...(typeof signature === 'object' && signature?.url
        ? { signatureImageUrl: signature.url }
        : {}),
    };
    if (Object.keys(info).length > 0) map[row._id] = info;
  }
  return map;
};

// 재발행 쿠폰 생성 (Admin)
export interface CreateAdditionalCouponsDto {
  giftOrderId: string;
  count: number;
  giftItemId?: string;
}

export const createAdditionalCoupons = async (
  dto: CreateAdditionalCouponsDto,
): Promise<GiftCouponForStore[]> => {
  const response = await api_.post<GiftCouponForStore[]>(
    `/${LowerRole.Admin}/gift-coupon/additional`,
    dto,
  );
  return response.data;
};

// 재발행 쿠폰 폐기 (Admin) - DB에서 완전 삭제
export const deleteAdditionalCoupon = async (id: string): Promise<void> => {
  await api_.delete(`/${LowerRole.Admin}/gift-coupon/additional/${id}`);
};

// 재발행 쿠폰 수신자 전화번호 저장 (Admin - 기존 patch 방식)
export const updateCouponAdditionalRecipientPhone = async (
  id: string,
  additionalRecipientPhone: string,
): Promise<GiftCouponForStore> => {
  const response = await api_.patch<GiftCouponForStore>(`/admin/gift-coupon/${id}`, {
    additionalRecipientPhone,
  });
  return response.data;
};

// 확인번호 + 메모 함께 업데이트 (Admin)
export const updateCouponPhoneAndMemo = async (
  id: string,
  data: { additionalRecipientPhone?: string; memo?: string },
): Promise<GiftCouponForStore> => {
  const response = await api_.patch<GiftCouponForStore>(`/admin/gift-coupon/${id}`, data);
  return response.data;
};

// 재발행 쿠폰 수신자 정보 변경 (Store - 전용 엔드포인트)
export const updateAdditionalRecipient = async (
  id: string,
  phoneNumber: string,
  role: GiftStoreRole,
  memo?: string,
): Promise<GiftCouponForStore> => {
  const response = await api_.patch<GiftCouponForStore>(
    `/${role}/gift-coupon/additional-recipient/${id}`,
    { phoneNumber, ...(memo ? { memo } : {}) },
  );
  return response.data;
};

/**
 * 카카오톡 회수(잘못 보낸) 케이스에서 일반 쿠폰의 수령자 번호를 잠그기 위한 엔드포인트.
 * 백엔드는 `additionalRecipientPhone` 필드를 업데이트하며 (재발행 쿠폰의
 * `additional-recipient` 와 동일하게 `memo` 동봉 시 메모도 함께 저장), 이후 redeem 시
 * 이 번호로만 본인 인증을 허용해 다른 수신자가 받지 못하도록 막는다.
 */
export const updateCouponRestrictedPhone = async (
  id: string,
  phoneNumber: string,
  role: GiftStoreRole,
  memo?: string,
): Promise<GiftCouponForStore> => {
  const response = await api_.patch<GiftCouponForStore>(
    `/${role}/gift-coupon/restricted-phone/${id}`,
    { phoneNumber, ...(memo ? { memo } : {}) },
  );
  return response.data;
};

// 쿠폰 폐기만 (재발행 없이) (Admin)
export interface DisposeCouponsDto {
  couponIds: string[];
  reason?: string;
}

export const disposeCoupons = async (dto: DisposeCouponsDto): Promise<GiftCoupon[]> => {
  const response = await api_.post<GiftCoupon[]>(
    `/${LowerRole.Admin}/gift-coupon/dispose`,
    dto,
  );
  return response.data;
};

// 쿠폰 폐기 + 재발급 (Admin)
export interface DisposeAndReissueCouponsDto {
  couponIds: string[];
  reason?: string;
}

export interface DisposeAndReissueResult {
  disposed: GiftCoupon[];
  reissued: GiftCoupon[];
}

export const disposeAndReissueCoupons = async (
  dto: DisposeAndReissueCouponsDto,
): Promise<DisposeAndReissueResult> => {
  const response = await api_.post<DisposeAndReissueResult>(
    `/${LowerRole.Admin}/gift-coupon/dispose-and-reissue`,
    dto,
  );
  return response.data;
};

// additional → reissued 승격 (Admin)
export const promoteToReissued = async (id: string): Promise<GiftCouponForStore> => {
  const response = await api_.patch<GiftCouponForStore>(
    `/${LowerRole.Admin}/gift-coupon/${id}/promote-to-reissued`,
  );
  return response.data;
};

// 발송매체(deliveryChannel)만 수정 (Admin)
export const updateCouponDeliveryChannel = async (
  id: string,
  deliveryChannel: DeliveryChannel | '',
): Promise<GiftCouponForStore> => {
  const response = await api_.patch<GiftCouponForStore>(`/admin/gift-coupon/${id}`, {
    'gifticon.deliveryChannel': deliveryChannel,
  });
  return response.data;
};

// 발송 정보(도매가/매체 등) 수정 (Admin) - 전달된 필드만 patch
export interface UpdateDeliveredCouponFieldsDto {
  /** 미지정(undefined)이면 변경 안함, 빈 문자열이면 매체 비움 */
  deliveryChannel?: DeliveryChannel | '';
  /** 미지정(undefined)이면 변경 안함 */
  priceWhole?: number;
}

export const updateDeliveredCouponFields = async (
  id: string,
  dto: UpdateDeliveredCouponFieldsDto,
): Promise<GiftCouponForStore> => {
  const body: Record<string, unknown> = {};
  if (dto.deliveryChannel !== undefined) {
    body['gifticon.deliveryChannel'] = dto.deliveryChannel;
  }
  if (dto.priceWhole !== undefined) {
    body['gifticon.priceWhole'] = dto.priceWhole;
  }
  const response = await api_.patch<GiftCouponForStore>(`/admin/gift-coupon/${id}`, body);
  return response.data;
};

// 발송일시(deliveredAt)만 수정 (Admin)
export const updateCouponDeliveredAt = async (
  id: string,
  deliveredAt: string,
): Promise<GiftCouponForStore> => {
  const response = await api_.patch<GiftCouponForStore>(`/admin/gift-coupon/${id}`, {
    'gifticon.deliveredAt': deliveredAt,
  });
  return response.data;
};

// 일반 환불 (미발송 쿠폰)
export interface RefundCouponDto {
  couponId: string;
  amount: number;
}

export const refundCoupon = async (dto: RefundCouponDto): Promise<GiftCoupon> => {
  const response = await api_.post<GiftCoupon>(`/admin/gift-coupon/refund`, dto);
  return response.data;
};

// 발송 후 환불 (이미 발송된 쿠폰)
export interface RefundAfterDeliveryDto {
  couponId: string;
  amount: number;
}

export const refundAfterDelivery = async (
  dto: RefundAfterDeliveryDto,
): Promise<GiftCoupon> => {
  const response = await api_.post<GiftCoupon>(
    `/admin/gift-coupon/refund-after-delivery`,
    dto,
  );
  return response.data;
};

// 환불/연장 Issue 발행 (Admin → 고객에게 신청 링크 허용)
export interface IssueCouponDto {
  couponId: string;
  type: 'refund' | 'renewal';
}

export const issueCoupon = async (dto: IssueCouponDto): Promise<GiftCoupon> => {
  const response = await api_.post<GiftCoupon>(`/admin/gift-coupon/issue`, dto);
  return response.data;
};

// Issue 취소 (Admin)
export const cancelIssueCoupon = async (id: string): Promise<GiftCoupon> => {
  const response = await api_.delete<GiftCoupon>(`/admin/gift-coupon/issue/${id}`);
  return response.data;
};

// Issued 목록 조회 (Admin)
export const findIssuedCoupons = async (): Promise<GiftCoupon[]> => {
  const response = await api_.get<GiftCoupon[]>(`/admin/gift-coupon/issued`);
  return response.data;
};

// 환불 승인 (Admin)
export interface ApproveRefundDto {
  couponId: string;
  amount: number;
}

export const approveRefund = async (dto: ApproveRefundDto): Promise<GiftCoupon> => {
  const response = await api_.post<GiftCoupon>(`/admin/gift-coupon/approve-refund`, dto);
  return response.data;
};

// 연장 승인 (Admin)
export interface ApproveRenewalDto {
  couponId: string;
  priceWhole: number;
  priceRetail: number;
  priceRebate: number;
  deliveryChannel: string;
}

export const approveRenewal = async (dto: ApproveRenewalDto): Promise<GiftCoupon> => {
  const response = await api_.post<GiftCoupon>(`/admin/gift-coupon/approve-renewal`, dto);
  return response.data;
};

// Public: 환불 신청 (고객이 링크에서 작성)
export interface RefundRequestDto {
  phoneNumber: string;
  name: string;
  bank: string;
  accountNumber: string;
  accountHolder: string;
  signatureImage: string;
}

export const requestRefund = async (
  couponId: string,
  dto: RefundRequestDto,
): Promise<GiftCoupon> => {
  const response = await api_.post<GiftCoupon>(
    `/${LowerRole.Public}/gift-coupon/refund-request/${couponId}`,
    dto,
  );
  return response.data;
};

// 연장쿠폰 생성
export interface CreateRenewalCouponDto {
  originalCouponId: string;
  recipientPhoneNumber?: string;
}

export const createRenewalCoupon = async (
  dto: CreateRenewalCouponDto,
): Promise<GiftCoupon> => {
  const response = await api_.post<GiftCoupon>(`/admin/gift-coupon/renewal`, dto);
  return response.data;
};

// 연장쿠폰 삭제
export const deleteRenewalCoupon = async (id: string): Promise<void> => {
  await api_.delete(`/admin/gift-coupon/renewal/${id}`);
};

// Public: 연장쿠폰 신청 (고객이 링크에서 작성)
export interface RenewalRequestDto {
  phoneNumber: string;
  name: string;
  signatureImage: string;
}

export const requestRenewal = async (
  couponId: string,
  dto: RenewalRequestDto,
): Promise<GiftCoupon> => {
  const response = await api_.post<GiftCoupon>(
    `/${LowerRole.Public}/gift-coupon/renewal-request/${couponId}`,
    dto,
  );
  return response.data;
};

// 번호 인증 코드 발송
export const sendVerificationCode = async (
  dto: VerificationSendDto,
): Promise<VerificationSendResponse> => {
  const response = await api_.post<VerificationSendResponse>(
    `/${LowerRole.Public}/gift-coupon/verification/send`,
    dto,
  );
  return response.data;
};

// 번호 인증 코드 확인
export const confirmVerificationCode = async (
  dto: VerificationConfirmDto,
): Promise<VerificationConfirmResponse> => {
  const response = await api_.post<VerificationConfirmResponse>(
    `/${LowerRole.Public}/gift-coupon/verification/confirm`,
    dto,
  );
  return response.data;
};

// ─── 추가교환 (중복 공유 쿠폰) ───

/**
 * 충당 가능 재고 조회 (Admin) — 추가교환 열기 전 판단용.
 * 같은 주문·상품의 미전달(Available) 쿠폰을 반환한다.
 * :id = 원본 쿠폰 _id
 */
export interface AvailableInventoryResponse {
  giftItem?: { _id: string; name?: string } | string;
  availableCount: number;
  coupons: GiftCouponSummary[];
}

export const getCouponAvailableInventory = async (
  id: string,
): Promise<AvailableInventoryResponse> => {
  const { data } = await api_.get<AvailableInventoryResponse>(
    `/${LowerRole.Admin}/gift-coupon/${id}/available-inventory`,
  );
  return data;
};

/**
 * 추가교환 열기 (Admin/GiftAgent) — :id = 원본 쿠폰 _id.
 * 재오픈 시 allowed만 조정, used 보존. allowed < used면 백엔드가 거부.
 */
export interface OpenExtraRedeemDto {
  allowed: number;
  reason?: string;
  expiresAt?: string; // ISO string · 미지정 시 무기한
}

export const openExtraRedeem = async (
  id: string,
  dto: OpenExtraRedeemDto,
): Promise<GiftCouponForStore> => {
  const { data } = await api_.post<GiftCouponForStore>(
    `/${LowerRole.Admin}/gift-coupon/${id}/extra-redeem/open`,
    dto,
  );
  return data;
};

/** 추가교환 닫기 (Admin/GiftAgent) — extraRedeem 제거 */
export const closeExtraRedeem = async (id: string): Promise<GiftCouponForStore> => {
  const { data } = await api_.patch<GiftCouponForStore>(
    `/${LowerRole.Admin}/gift-coupon/${id}/extra-redeem/close`,
  );
  return data;
};

/**
 * 수신자 추가신청 (Public) — :id = 원본 쿠폰 _id.
 * 번호인증이 원본 쿠폰 verification과 일치해야 하며, 스폰된 새 쿠폰(Requested)을 반환한다.
 */
export const requestExtraRedeem = async (
  id: string,
  dto: GiftCouponRequestDto,
): Promise<GiftCouponForStore> => {
  const { data } = await api_.post<GiftCouponForStore>(
    `/${LowerRole.Public}/gift-coupon/extra-redeem/${id}`,
    dto,
  );
  return data;
};

/**
 * 수신자 추가신청 — 나중에 받기 (Public) — :id = 원본 쿠폰 _id.
 * 본인 전용 spawn 쿠폰을 Available(예약) 상태로 생성해 반환한다.
 * 같은 주문·번호로 이미 Available spawn이 있으면 그것을 반환(멱등). 응답의 code로
 * 본인 쿠폰 URL을 만들어 이동/저장한다.
 */
export const saveExtraRedeemLater = async (
  id: string,
  dto: GiftCouponSaveRecipientDto,
): Promise<GiftCouponForStore> => {
  const { data } = await api_.post<GiftCouponForStore>(
    `/${LowerRole.Public}/gift-coupon/extra-redeem/${id}/save-later`,
    dto,
  );
  return data;
};

// ─── 추가결제 대기건 처리 (Admin) ───

/**
 * (A) 추가결제 후 발송 — :id = 스폰 쿠폰 _id.
 * extraPayment.isPaid=true 로 전환되어 발송대기로 이동.
 */
export interface ExtraPaymentDto {
  method?: string; // 예: "Card" | "BankTransfer"
  amount?: number;
  receiptId?: string;
}

export const payExtraPayment = async (
  id: string,
  dto: ExtraPaymentDto,
): Promise<GiftCouponForStore> => {
  const { data } = await api_.patch<GiftCouponForStore>(
    `/${LowerRole.Admin}/gift-coupon/${id}/extra-payment`,
    dto,
  );
  return data;
};

/**
 * (B) 대체발송 후보 조회 — :id = 스폰 쿠폰 _id.
 * 같은 주문·상품의 Available 쿠폰 배열 (seq 오름차순).
 */
export const getDonorCandidates = async (id: string): Promise<GiftCouponSummary[]> => {
  const { data } = await api_.get<GiftCouponSummary[]>(
    `/${LowerRole.Admin}/gift-coupon/${id}/donor-candidates`,
  );
  return data;
};

/**
 * (B) 기구매 미전달 쿠폰으로 대체발송 — :id = 스폰 쿠폰 _id.
 * 스폰 생존 + 상속 방식: 스폰이 donor의 seq·purchasedAt·expiresAt를 상속하고
 * settledBy='inventory'·absorbedDonor=donorId로 그대로 발송대기(Requested) 유지된다.
 * coupon: 생존한 스폰(=실제 발송될 단일 live 레코드).
 * disposed: 흡수되어 폐기된 donor (Disposed, 통계 제외).
 */
export interface FulfillFromExistingResult {
  coupon: GiftCoupon;
  disposed: GiftCoupon;
}

export const fulfillFromExisting = async (
  id: string,
  donorCouponId: string,
): Promise<FulfillFromExistingResult> => {
  const { data } = await api_.post<FulfillFromExistingResult>(
    `/${LowerRole.Admin}/gift-coupon/${id}/fulfill-from-existing`,
    { donorCouponId },
  );
  return data;
};

/**
 * (C) 추가결제 대기건 취소 (Admin) — :id = 스폰 쿠폰 _id.
 * 미정산·Requested·미결제 스폰 쿠폰만 취소 가능. 백엔드가 부수효과를 되돌리고
 * (흡수 donor 복원 / extraPayments 항목 제거 / 원본 extraRedeem.used -1) 스폰 쿠폰을
 * hard-delete 한다. 실제 결제된(amount>0) 건은 환불 흐름으로 따로 처리하므로 거부된다.
 */
export const cancelSpawnedCoupon = async (id: string): Promise<{ deleted: boolean }> => {
  const { data } = await api_.delete<{ deleted: boolean }>(
    `/${LowerRole.Admin}/gift-coupon/spawned/${id}`,
  );
  return data;
};

// ─── 답례 인사 일괄 발송 (Admin/GiftAgent) ───

/**
 * 선택한 쿠폰들의 additionalRecipientPhone(확인번호)로 답례글 LMS를 일괄 발송한다.
 * - 번호 없는 쿠폰은 서버가 skipped(NO_PHONE)로 건너뜀(에러 아님).
 * - 일부 실패해도 끝까지 시도 후 집계를 반환하며 HTTP는 2xx.
 * - 본문 하단에 "답례품 받기" 링크가 자동 부착된다.
 *   (couponIds(_id)로 조회한 각 쿠폰의 code로 서버가 링크를 생성)
 * - 링크 도메인: 서버 env GIFT_RECIPIENT_BASE_URL 설정 시 그 도메인의 단축경로
 *   ({도메인}/coupon/{couponCode})로 조립되며 baseUrl은 무시된다. 미설정 구서버는
 *   {baseUrl}/gift-store/coupon/{couponCode} 폴백이므로 baseUrl은 항상 전송할 것.
 */
export interface SendCourtesyMessageDto {
  couponIds: string[];
  message: string;
  baseUrl: string;
}

export interface CourtesyMessageSkipped {
  couponId: string;
  /** NOT_FOUND(쿠폰 없음) | NO_PHONE(번호 없음) */
  reason: string;
}

export interface CourtesyMessageFailed {
  couponId: string;
  /** 발송 API 에러 메시지 */
  reason: string;
}

export interface SendCourtesyMessageResult {
  /** 중복 제거 후 처리 대상 수 */
  total: number;
  sentCount: number;
  skippedCount: number;
  failedCount: number;
  sent: string[];
  skipped: CourtesyMessageSkipped[];
  failed: CourtesyMessageFailed[];
}

export const sendCourtesyMessage = async (
  dto: SendCourtesyMessageDto,
): Promise<SendCourtesyMessageResult> => {
  const { data } = await api_.post<SendCourtesyMessageResult>(
    `/${LowerRole.Admin}/gift-coupon/send-courtesy-gift-message`,
    dto,
  );
  return data;
};

// ─── 품절 재선택 안내 (Admin ↔ 수령자) ───
// 스펙: docs/gift-coupon-reselect-soldout-spec.md
// ⚠️ 백엔드 반영 대기 — 구서버는 아래 5개 엔드포인트에서 404를 반환한다. 호출부는
//    404를 "미배포"로 해석해 기능을 조용히 숨긴다(발송대기 화면이 깨지면 안 됨).

/** 품절 안내 일괄 발송 요청 — 대상은 발송대기(Requested) 쿠폰 */
export interface SendReselectNoticeDto {
  couponIds: string[];
  /** 내부 사유 메모 (수령자 미노출) */
  reason?: string;
  /** 안내 본문 — 미지정 시 서버 기본 문구 */
  message?: string;
  /** 품절 안내 이미지 (ImageAsset _id) — 옵셔널 */
  noticeImage?: string;
  /**
   * 재선택 기한 (ISO). 미지정 시 서버가 안내 발송 +14일로 설정한다.
   * 전상법상 공급곤란 통지 후 환급 의무가 있어 기한 고지가 필수 — 문자 말미에
   * "○월 ○일까지 미선택 시 상주님께 전액 환불"이 서버에서 항상 덧붙는다.
   */
  deadlineAt?: string;
  /** 구서버(GIFT_RECIPIENT_BASE_URL 미설정) 폴백용 — 항상 전송 */
  baseUrl: string;
}

export interface ReselectNoticeSkipped {
  couponId: string;
  /** NOT_FOUND | NO_PHONE | INVALID_STATUS */
  reason: string;
}

export interface ReselectNoticeFailed {
  couponId: string;
  reason: string;
}

export interface SendReselectNoticeResult {
  total: number;
  sentCount: number;
  skippedCount: number;
  failedCount: number;
  sent: string[];
  skipped: ReselectNoticeSkipped[];
  failed: ReselectNoticeFailed[];
}

export const sendReselectNotice = async (
  dto: SendReselectNoticeDto,
): Promise<SendReselectNoticeResult> => {
  const { data } = await api_.post<SendReselectNoticeResult>(
    `/${LowerRole.Admin}/gift-coupon/reselect-notice`,
    dto,
  );
  return data;
};

/**
 * 안내 문자만 재발송 (상태 변화 없음) — 저장된 본문·이미지 재사용.
 * 서버는 `noticeSentAt`·`noticeCount`·`deadlineAt`을 갱신하지 않는다
 * (대기 경과시간과 이미 고지한 기한이 재발송으로 리셋되면 안 됨).
 * 응답은 단건이라 일괄 발송과 형태가 다르다 — `{ sent }` 불리언.
 * Admin 한정(GiftAgent 403).
 */
export interface ResendReselectNoticeResult {
  sent: boolean;
  /** 실패 사유 (sent=false일 때) */
  reason?: string;
}

export const resendReselectNotice = async (
  id: string,
  dto: { message?: string; baseUrl: string },
): Promise<ResendReselectNoticeResult> => {
  const { data } = await api_.post<ResendReselectNoticeResult>(
    `/${LowerRole.Admin}/gift-coupon/${id}/reselect-notice/resend`,
    dto,
  );
  return data;
};

/**
 * 안내 철회 (오처리 복구) — ReselectRequired → Requested 로 되돌린다.
 * `reselect`는 지워지지 않고 `status: 'Resolved'`로 마감되어 이력이 남는다. (Admin·GiftAgent)
 */
export const cancelReselectNotice = async (id: string): Promise<GiftCouponForStore> => {
  const { data } = await api_.patch<GiftCouponForStore>(
    `/${LowerRole.Admin}/gift-coupon/${id}/reselect/cancel`,
    {},
  );
  return data;
};

/**
 * (수령자) 재선택 — :id = 쿠폰 _id (링크 보안레벨 2).
 * 기존 번호인증·서명을 그대로 승계하므로 재인증을 요구하지 않는다.
 */
export const reselectCoupon = async (
  id: string,
  selectedChoiceUnit: string,
): Promise<GiftCouponForPublic> => {
  const { data } = await api_.post<GiftCouponForPublic>(
    `/${LowerRole.Public}/gift-coupon/reselect/${id}`,
    { selectedChoiceUnit },
  );
  return data;
};

/** (수령자) 취소(선물 사양) 요청 — 관리자 취소요청 큐로 이동 */
export const requestReselectCancel = async (
  id: string,
  reason?: string,
): Promise<GiftCouponForPublic> => {
  const { data } = await api_.post<GiftCouponForPublic>(
    `/${LowerRole.Public}/gift-coupon/reselect/${id}/cancel-request`,
    reason ? { reason } : {},
  );
  return data;
};

// ⚠️ 선물거절 취소 안내(구매자에게 "수령인이 받지 않아 결제가 취소되었다")를 위한
//    별도 엔드포인트는 **만들지 않는다**. 백엔드가 부분취소 실행 경로 안에서
//    `sendGiftCouponCanceledMessageSafe`로 자동 발송하도록 구현했다
//    (gift-coupon.core.service.ts cancelCouponsByIds). 여기서 따로 부르면 이중 발송이 된다.
//    스펙: docs/gift-coupon-reselect-soldout-spec.md §15

// ─── 이용안내(상품권 교환·잔액 유의사항 법적 고지) 일괄 발송 (Admin) ───

/**
 * giftOrder 단위로, 이미 교환(Requested/Delivered)했고 신청자(수신) 번호가 있는
 * 쿠폰들에 이용안내(교환·잔액 유의사항) LMS를 일괄 발송한다.
 * - 교환 전이거나 번호 없는 쿠폰은 서버가 skipped로 건너뜀(에러 아님).
 * - 일부 실패해도 끝까지 시도 후 집계를 반환(HTTP 2xx).
 * - 본문은 서버 기본 문구(DEFAULT_GUIDE_MESSAGE)를 사용하며, message로 덮어쓸 수 있다.
 * - excludePhones에 담긴 번호는 발송 제외(서버가 숫자만 비교 — 하이픈/공백 무관).
 *   엔드포인트: POST /admin/gift-order/:id/send-coupon-guide-message
 */
export interface SendUsageGuideDto {
  giftOrderId: string;
  /** 안내 본문 직접 지정 (미지정 시 서버 기본 이용안내 문구 사용) */
  message?: string;
  /** 발송 제외할 번호 목록 (숫자만/하이픈 포함 무관 — 서버가 숫자만 비교) */
  excludePhones?: string[];
}

export interface UsageGuideSkipped {
  couponId: string;
  /** NOT_FOUND(쿠폰 없음) | NO_PHONE(번호 없음) 등 */
  reason: string;
}

export interface UsageGuideFailed {
  couponId: string;
  /** 발송 API 에러 메시지 */
  reason: string;
}

export interface SendUsageGuideResult {
  /** 중복 제거 후 처리 대상 수 */
  total: number;
  sentCount: number;
  skippedCount: number;
  failedCount: number;
  sent: string[];
  skipped: UsageGuideSkipped[];
  failed: UsageGuideFailed[];
}

export const sendCouponUsageGuide = async (
  dto: SendUsageGuideDto,
): Promise<SendUsageGuideResult> => {
  const { giftOrderId, message, excludePhones } = dto;
  const body: Pick<SendUsageGuideDto, 'message' | 'excludePhones'> = {};
  if (message) body.message = message;
  if (excludePhones?.length) body.excludePhones = excludePhones;
  const { data } = await api_.post<SendUsageGuideResult>(
    `/${LowerRole.Admin}/gift-order/${giftOrderId}/send-coupon-guide-message`,
    body,
  );
  return data;
};
